import { useLazyQuery } from '@apollo/react-hooks';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { loader } from 'graphql.macro';
import { debounce } from 'lodash';
import { MockApolloClient } from 'mock-apollo-client';
import React, { useEffect, useRef, useState } from 'react';

import { colors } from 'src/themes/colors';

const CLIENT_SEARCH_SELECTION_QUERY = loader('./documents/ClientSearchSelectionQuery.gql');
export const MAX_RESULTS_RENDERED = 5;

interface ClientResult {
  memberUuid: string;
  memberFirstName: string;
  memberLastName: string;
  memberEmail: string;
  companyName: string;
}

export interface ClientSearchSectionQuery {
  clients: {
    edges: Array<{
      node: ClientResult;
    }>;
  };
}
interface ClientSearchSelectionVars {
  search: string;
}

export interface Props {
  // Note: the correct way to mock this would be to use the client from the GraphQL provider,
  // but this would also require either an API gateway/Federation, which we don't currently have.
  // In order to make this component testable, take client as a prop so we can mock gql data.
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onClientSelect: (clientUuid: any) => void;
  canShowError?: boolean;
}

export enum ClientSearchSelectionTestID {
  SearchInput = 'search-input',
  SelectedClientDisplay = 'selected-client-display',
  SearchResults = 'search-results',
  SelectableClient = 'selectable-client',
  ErrorText = 'error-text',
}
export const ClientSearchSelection: React.FC<Props> = ({
  memberClient,
  onClientSelect,
  canShowError,
}) => {
  const [isInputting, setIsInputting] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputWidth, setInputWidth] = useState(0);
  const [selectedClient, setSelectedClient] = useState<ClientResult>();

  const inputRef = useRef<HTMLInputElement>(null);

  const [getClientSearchResults, { data, called }] = useLazyQuery<
    ClientSearchSectionQuery,
    ClientSearchSelectionVars
  >(CLIENT_SEARCH_SELECTION_QUERY, {
    client: memberClient,
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Persist the event so that when debounce kicks in, the value still exists.
    event.persist();
    setSearchTerm(event.target.value);
    debounce(() => getClientSearchResults({ variables: { search: event.target.value } }), 500)();
  };

  const handleClientSelect = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const selectedClientEdge = data?.clients.edges.find(
      (client) => client.node.memberUuid === event.currentTarget.value,
    );
    const memberName =
      selectedClientEdge?.node.memberFirstName + ' ' + selectedClientEdge?.node.memberLastName;
    setIsInputting(false);
    setSearchTerm(memberName || '');
    setSelectedClient(selectedClientEdge?.node);
    onClientSelect(event.currentTarget.value);
  };

  useEffect(() => {
    if (inputRef.current?.clientWidth) {
      setInputWidth(inputRef.current.clientWidth);
    }
  }, [inputRef]);

  // Focus on search input when the client name is clicked
  useEffect(() => {
    if (!isInputting) {
      return;
    }
    inputRef?.current?.focus();
  }, [isInputting]);

  // Detect click-out to hide options (onBlur cancels onClick when another client is selected)
  useEffect(() => {
    const onClickHandler = (event: MouseEvent) => {
      const target = event?.target as Element;
      if (selectedClient && !target.closest('#client-search-input')) {
        setIsInputting(false);
      }
    };

    if (isInputting) {
      document.addEventListener('click', onClickHandler);
    }

    return () => {
      document.removeEventListener('click', onClickHandler);
    };
  }, [isInputting, selectedClient]);

  const shouldShowError = canShowError && !selectedClient;

  const noClientsFound = searchTerm && data?.clients.edges.length === 0 && called;

  const defaultValue = selectedClient
    ? `${selectedClient?.memberFirstName} ${selectedClient?.memberLastName}`
    : undefined;

  return (
    <div id="client-search-input" className={noClientsFound ? 'relative' : ''}>
      {isInputting ? (
        <>
          <input
            ref={inputRef}
            data-testid={ClientSearchSelectionTestID.SearchInput}
            className={`text-sm w-full p-2 pl-3 font-medium text-gray-800 bg-transparent rounded-md border hover:border-blue-400 ${
              shouldShowError ? 'border-red-600' : 'border-gray-300'
            }`}
            placeholder="Select your client"
            list="client-options"
            style={{ outlineColor: shouldShowError ? colors.red2 : colors.blue6 }}
            onChange={handleInputChange}
            defaultValue={defaultValue}
          />
          {!(searchTerm && data?.clients.edges.length) && shouldShowError ? (
            <p
              data-testid={ClientSearchSelectionTestID.ErrorText}
              className="mt-2 font-xs text-red-600 font-medium"
            >
              Select client
            </p>
          ) : null}

          {noClientsFound ? (
            <div className="absolute bg-white rounded-md border border-gray-300 shadow-md mt-1 px-1 py-6 z-10 text-center">
              <p className="font-bold text-gray-850 mb-1">
                It seems like we couldn’t find your client
              </p>
              <p className="font-medium text-gray-600">
                If this an error, reach out to us at{' '}
                <a
                  className="text-blue-200 font-medium"
                  href="mailto:provider@joinmodernhealth.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  provider@joinmodernhealth.com
                </a>
              </p>
            </div>
          ) : null}

          {(searchTerm && data?.clients.edges.length && (
            <div
              className="absolute bg-white rounded-md border border-gray-300 shadow-md mt-1 p-2 z-10"
              style={{
                minWidth: inputWidth || 'auto',
              }}
            >
              <ul>
                {data?.clients.edges.slice(0, MAX_RESULTS_RENDERED).map((edge) => (
                  <li key={edge.node.memberUuid}>
                    <button
                      data-testid={ClientSearchSelectionTestID.SelectableClient}
                      className="font-medium text-gray-800 w-full hover:rounded-md hover:bg-gray-100 p-1 py-2"
                      onClick={handleClientSelect}
                      value={edge.node.memberUuid}
                    >
                      <div className="flex justify-between">
                        <div className="inline-flex pr-2 text-gray-800">
                          {edge.node.memberFirstName} {edge.node.memberLastName}
                        </div>
                        <div className="inline-flex font-normal text-gray-500">{`${edge.node.companyName}, ${edge.node.memberEmail}`}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )) ||
            null}
        </>
      ) : (
        <button
          data-testid={ClientSearchSelectionTestID.SelectedClientDisplay}
          onClick={() => setIsInputting(true)}
          className="flex justify-between text-sm w-full p-2 font-extrabold bg-transparent rounded-md border border-gray-300"
        >
          {selectedClient && (
            <>
              <div className="inline-flex pr-2 text-gray-800 whitespace-no-wrap">
                {selectedClient.memberFirstName} {selectedClient.memberLastName}
              </div>
              <div className="inline-flex font-normal text-gray-500 truncate">{`${selectedClient.companyName}, ${selectedClient.memberEmail}`}</div>
            </>
          )}
        </button>
      )}
    </div>
  );
};
